import debounce from "lodash/debounce";
import { useActivityUpdatedHandler } from "PFApp/booking/hooks/use_activity_updated_handler";
import { useProfilesAvailabilitiesUpdatedHandler } from "PFApp/booking/hooks/use_profile_availability_updated_handler";
import { useNativeNotificationsHandler } from "PFApp/bootstrapper/use_native_notifications_handler";
import { CHAT_NOTIFICATION_CATEGORIES } from "PFApp/constants/notifications";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { gidToId, gidToType } from "PFCore/helpers/gid";
import { useCurrentAccount } from "PFCore/hooks/queries/account/use_current_account";
import { useChatInvalidate } from "PFCore/hooks/queries/chat/use_chat_invalidate";
import { useNotificationsInvalidate } from "PFCore/hooks/queries/notifications/use_notifications_invalidate";
import { useProfileInvalidate } from "PFCore/hooks/queries/profile/use_profile_invalidate";
import { fetchNotification } from "PFCore/services/notifications/fetch_notification";
import { NotificationCategoryType } from "PFTypes/notifications";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useDebounce } from "../../helpers/use_debounce";
import { useCurrentProfile } from "../../hooks/queries/profile";
import { usePusherEvent } from "./use_pusher_event";

export const useGlobalEvents = () => {
  const { t } = useTranslation();
  const { data: currentProfile } = useCurrentProfile();
  const { invalidateCurrentProfile } = useProfileInvalidate();
  const { data: currentAccount } = useCurrentAccount();

  const { showGrowl } = useGrowlStoreActions();
  const {
    handleBookingModuleProfilesAvailabilitiesUpdatedForActivityWarnings,
    handleBookingModuleProfilesAvailabilitiesUpdated
  } = useProfilesAvailabilitiesUpdatedHandler();
  const handleBookingModuleActivityUpdated = useActivityUpdatedHandler();
  const { invalidate: invalidateNotifications, invalidateNotificationsGroups } = useNotificationsInvalidate();
  const { invalidateAllConversations } = useChatInvalidate();

  const refetchUser = useDebounce(() => invalidateCurrentProfile(), 1000);
  const refetchConversations = useDebounce(() => invalidateAllConversations(), 3000);
  const refetchNotifications = useDebounce(() => {
    invalidateNotifications({ excludedCategories: CHAT_NOTIFICATION_CATEGORIES });
    invalidateNotificationsGroups();
  }, 1000);
  const refetchChatNotifications = useDebounce(
    () => invalidateNotifications({ categories: CHAT_NOTIFICATION_CATEGORIES }),
    1000
  );

  const { handleCreateNativeNotification } = useNativeNotificationsHandler();

  const updateCurrentProfile = useCallback(
    (data) => {
      if (data["object_name"] === "profile") {
        refetchUser();
      }
    },
    [refetchUser]
  );

  const growlPopup = useCallback(
    (data) => {
      showGrowl({ message: t(data.message, data) });
    },
    [showGrowl, t]
  );

  const handleNotificationCreated = useCallback(
    async (data) => {
      const type = gidToType(data.resource_gid).toLowerCase();
      const id = gidToId(data.resource_gid);
      if (type !== "notification") {
        return;
      }
      const notification = await fetchNotification(id);
      handleCreateNativeNotification(notification);
      if (notification.categoryType === NotificationCategoryType.Message) {
        refetchChatNotifications();
        refetchConversations();
      } else {
        refetchNotifications();
      }
    },
    [handleCreateNativeNotification, refetchChatNotifications, refetchConversations, refetchNotifications]
  );

  usePusherEvent(`private-profile-${currentProfile.id}`, "refresh", updateCurrentProfile);
  usePusherEvent(`private-profile-${currentProfile.id}`, "popup", growlPopup);
  usePusherEvent(`private-profile-${currentProfile.id}`, "create", handleNotificationCreated);

  usePusherEvent(
    `private-account-${currentAccount.id}`,
    "profiles_availabilities_updated",
    handleBookingModuleProfilesAvailabilitiesUpdated
  );
  // additional debounced callback for profile_availability_updated
  // to not multiply activity_warnings requests after each profile is updated
  usePusherEvent(
    `private-account-${currentAccount.id}`,
    "profiles_availabilities_updated",
    debounce(handleBookingModuleProfilesAvailabilitiesUpdatedForActivityWarnings, 5000)
  );

  usePusherEvent(
    `private-account-${currentAccount.id}`,
    "activity_updated",
    handleBookingModuleActivityUpdated
  );
  usePusherEvent(
    `private-account-${currentAccount.id}`,
    "activity_bookings_updated",
    handleBookingModuleActivityUpdated
  );
};
